// typescript
// file: `typescript/apps/front-end/src/main/react/header/sidebar/MainSideBar.tsx`
import React from "react";
import {
    Sidebar,
    SidebarContent,
    SidebarRail,
    useSidebar,
} from "@/components/ui/sidebar";
import {Localizer} from "@/main/react/Types";
import {trackSidebarOpened} from "@/main/analytics/analyticsEventsCore";
import {lazyWithRetry} from "@/lib/lazyWithRetry";
import {LazyControlBoundary} from "@/main/react/LazyControlBoundary";
import {ensureDeferredCatalogLoaded} from "@/main/messages/deferredCatalog";
import {useCurrentPathname} from "@/main/react/hooks/useCurrentPathname";
import {CREATE_DISCUSSION_PATH} from "@/main/navigation/composerRoutes";

export type MainSideBarViewProps = {
    loggedIn: boolean;
};

type LazyMainMenuViewProps = { localizer: Localizer; loggedIn: boolean };

/**
 * FI-2263 (r108): the menu BODY, off the startup graph. `MenuSideBarView.tsx` was ~55 KB of eager JS for a sidebar
 * that is closed on every page load (`SidebarProvider defaultOpen={false}` in `MainHeader`). The shell below (the
 * `Sidebar`, its rail and the toggle listener) stays eager; the body loads through `lazyWithRetry` (rule 26) and,
 * like the notifications drawer, waits for the deferred catalog half, because once this module is off the eager
 * graph `generate-split-catalogs.mjs` files its keys there.
 *
 * <p><b>Warm, then mount, never a mount toggle on a gesture.</b> Pointer, focus or touch on a sidebar DOOR (and the
 * browser's idle callback) only call `.preload()`, which starts the import and changes no state, so the door the
 * member is pressing is never swapped under the press (rulebook `AGENTS/frontend-runtime.md`, "warming a chunk must
 * not change the rendered tree"). The MOUNT happens when the sidebar is actually OPEN, which is the click's own
 * outcome, and the body then stays mounted so closing and reopening never refetches or loses submenu state.
 */
const LazyMainMenuView = lazyWithRetry<React.ComponentType<LazyMainMenuViewProps>>(
    () => import("@/main/react/header/sidebar/MenuSideBarView")
        .then((module) => ensureDeferredCatalogLoaded().then(() => ({
            default: module.MainMenuView as React.ComponentType<LazyMainMenuViewProps>,
        }))),
    "sidebar-menu-body",
);

/** Every control that opens the sidebar: the header's Menu button and avatar, and the phone bottom bar's menu tab. */
export const SIDEBAR_DOOR_SELECTOR =
    '.main-header-main-sidebar-button, .main-header-profile-image, [data-census-id="mobile-bottom-nav-tab-menu"]';

/** Preload-only warm-up rungs: a door's pointer, focus or touch, then idle. None of them sets state. */
function useWarmSidebarMenuBody(): void {
    React.useEffect(() => {
        const warmFromDoor = (event: Event) => {
            const target = event.target;
            if (target instanceof Element && target.closest(SIDEBAR_DOOR_SELECTOR)) {
                LazyMainMenuView.preload();
            }
        };
        const events = ["pointerover", "focusin", "touchstart"] as const;
        events.forEach((name) => document.addEventListener(name, warmFromDoor, {capture: true, passive: true}));

        const idleWindow = window as Window & {
            requestIdleCallback?: (callback: () => void) => number;
            cancelIdleCallback?: (handle: number) => void;
        };
        const idleHandle = typeof idleWindow.requestIdleCallback === "function"
            ? idleWindow.requestIdleCallback(() => LazyMainMenuView.preload())
            : null;

        return () => {
            events.forEach((name) => document.removeEventListener(name, warmFromDoor, {capture: true}));
            if (idleHandle !== null) {
                idleWindow.cancelIdleCallback?.(idleHandle);
            }
        };
    }, []);
}

/**
 * Mounts the lazy body once the sidebar has been OPEN (desktop `open` or phone `openMobile`), then keeps it mounted.
 * Module scope, like `ToggleListener`, so its type identity is stable (`check-no-nested-component-definitions.mjs`).
 */
const SidebarMenuBody: React.FC<LazyMainMenuViewProps> = ({localizer, loggedIn}) => {
    const {open, openMobile} = useSidebar();
    const [opened, setOpened] = React.useState(false);
    const isOpen = open || openMobile;
    React.useEffect(() => {
        if (isOpen && !opened) {
            setOpened(true);
        }
    }, [isOpen, opened]);

    if (!isOpen && !opened) {
        return null;
    }
    return (
        <LazyControlBoundary controlName="sidebar-menu-body" fallback={null}>
            <React.Suspense fallback={null}>
                <LazyMainMenuView localizer={localizer} loggedIn={loggedIn} />
            </React.Suspense>
        </LazyControlBoundary>
    );
};

/**
 * ⚠️ DECLARED AT MODULE SCOPE, DELIBERATELY. Do NOT move this back inside `MainSideBarInner`.
 *
 * <p>It lived in that component's body until 2026-08-23. A component declared inside another
 * component's body gets a NEW TYPE IDENTITY on every render of its owner, and React does not
 * reconcile across a changed type — it UNMOUNTS the old subtree and MOUNTS a fresh one. For this
 * component that meant the `useEffect` below tore down and re-registered a capture-phase `document`
 * click listener on every single render of the sidebar, which is the listener that decides whether a
 * click closes the drawer. Cheap to write, invisible in review, and paid forever.
 *
 * <p>It takes no props and reads everything it needs from `useSidebar()`, so hoisting it is
 * behaviour-preserving: same hook, same effect, same dependency list — it simply stops being a
 * different component each time.
 *
 * <p>This is the same class as the `asChild` churn AGENTS.md rule 31 is about, one level up: there the
 * REF identity changes per render, here the COMPONENT identity does, and a remount is strictly the
 * more expensive of the two. Guarded by `scripts/check-no-nested-component-definitions.mjs`.
 */
const ToggleListener: React.FC = () => {
    const { toggleSidebar, open, setOpen, openMobile, setOpenMobile, isMobile } = useSidebar();

    React.useEffect(() => {
        const headerSelector = ".main-header-profile-image, .main-header-main-sidebar-button";

        const onClick = (ev: MouseEvent) => {
            const target = (ev.target as Element | null);
            const clickedHeaderToggle = target?.closest(headerSelector);
            const currentlyOpen = isMobile ? openMobile : open;
            const setCurrentlyOpen = isMobile ? setOpenMobile : setOpen;

            if (clickedHeaderToggle) {
                ev.preventDefault();

                // SV-157 D9: an OPEN from the header's door (avatar or Menu, 768 px and up since the phone has
                // no header). The phone's door is the bottom bar's menu tab, which records its own surface.
                if (!currentlyOpen) {
                    trackSidebarOpened("header");
                }

                if (typeof setCurrentlyOpen === "function") {
                    setCurrentlyOpen(!currentlyOpen);
                } else if (typeof toggleSidebar === "function") {
                    toggleSidebar();
                }
                return;
            }

            const clickedInsideSidebar = !!(target?.closest("[data-main-sidebar]"));
            const clickedInsideSubMenu = !!(target?.closest('[role="menu"]'));
            if (clickedInsideSidebar || clickedInsideSubMenu) return;

            if (currentlyOpen) {
                if (typeof setCurrentlyOpen === "function") {
                    setCurrentlyOpen(false);
                } else if (typeof toggleSidebar === "function") {
                    toggleSidebar();
                }
            }
        };

        document.addEventListener("click", onClick, true);
        return () => document.removeEventListener("click", onClick, true);
    }, [toggleSidebar, open, setOpen, openMobile, setOpenMobile, isMobile]);

    return null;
};

/** The post composer's three shapes (`/create/discussion`, `/new`, `/{draftId}`), never a `/create/discussions` sibling. */
export function isComposerPathname(pathname: string): boolean {
    return pathname === CREATE_DISCUSSION_PATH || pathname.startsWith(`${CREATE_DISCUSSION_PATH}/`);
}

/**
 * `CREATE-PUBLISH-222` (r128, settle r125 A11 ⚑G-19): shadcn's `SidebarRail`, except on the composer while the
 * sidebar is collapsed offcanvas. There the rail is an invisible 16 CSS px "Toggle Sidebar" strip down the screen edge
 * that opens navigation over the draft on a stray click. Elsewhere, and on the composer once the sidebar is open, it is
 * exactly the rail it was. Module scope for the `ToggleListener` reason (`check-no-nested-component-definitions.mjs`);
 * the path comes from the one pathname reader (`useCurrentPathname`), never a private mirror (rule 31).
 */
const MainSidebarRail: React.FC = () => {
    const {open} = useSidebar();
    const pathname = useCurrentPathname();
    if (!open && isComposerPathname(pathname)) {
        return null;
    }
    return <SidebarRail />;
};

export function MainSideBarInner({
                                     localizer,
                                     loggedIn,
                                 }: { localizer: Localizer } & MainSideBarViewProps) {
    // r119 `2b`: warmed from the always-mounted shell. On a phone the body sits inside the lazy sheet, which renders
    // nothing until the first open, so warming from inside the body would wait for the open it exists to speed up.
    useWarmSidebarMenuBody();
    return (
        <>
            <ToggleListener />

            {/* NOTE: this `style` reaches the sidebar container on DESKTOP only. On mobile `Sidebar` renders a
                Radix `Sheet`, and the prop spreads onto its root rather than a DOM node, so every value here
                is dropped — the sheet sizes itself from `--seavien-app-viewport-height`. The bottom
                safe-area inset that used to live here therefore never applied on Android; it now sits on
                `SidebarLegalLinks`, which works on both branches. */}
            <Sidebar
                side={"right"}
                // r119 `2b` (FI-2868): the same doors preload the lazy phone sheet (rule 26, preload only).
                mobileSheetWarmSelector={SIDEBAR_DOOR_SELECTOR}
                // FI-3335: the phone sheet's dialog name, the header's own "Menu" (was hardcoded English "Sidebar").
                mobileSheetTitle={localizer("header.tooltip.menu")}
                className={"fixed right-0"}
                style={{
                    top: "calc(4rem + env(safe-area-inset-top) + var(--seavien-connection-banner-height, 0px))",
                    height: "calc(100vh - 4rem - env(safe-area-inset-top) - var(--seavien-connection-banner-height, 0px))",
                }}
            >
                {/* ONE scroll region, deliberately: the menu, the account action and the legal links all
                    live inside `SidebarContent`, so the scroll boundary and the visual end of the menu are
                    the same place. A pinned `SidebarFooter` under this (2026-08-08 → 2026-08-09) is what
                    made the list look finished several rows early. See
                    designs/01-now/2026-08-09-sidebar-single-scroll-and-home-rail-flush-rows-design.md §1. */}
                <div data-main-sidebar className="flex flex-col h-full">
                    <SidebarContent>
                        <SidebarMenuBody
                            localizer={localizer}
                            loggedIn={loggedIn}
                        />
                    </SidebarContent>

                    <MainSidebarRail />
                </div>
            </Sidebar>
        </>
    );
}
